// src/pages/AuthorsPage.jsx
import React, { useState } from "react";
import {
  Btn, Modal, SectionHeader, EmptyState, InfoBanner,
  Input, Textarea, FileUpload, ProgressBar,
} from "../components/UI";
import { uploadFile, deleteFile } from "../utils/uploadFile";
import { addDocument, deleteDocument } from "../hooks/useFirestore";

function AuthorForm({ onSave, onClose }) {
  const [form, setForm] = useState({
    name: "",
    designation: "",
    bio: "",
    imageFile: null,
    imagePreview: "",
  });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Author name is required."); return; }
    setError(""); setUploading(true);

    try {
      let imageURL = "";
      if (form.imageFile) {
        imageURL = await uploadFile(form.imageFile, "authors", p => setProgress(p));
      }
      setProgress(100);
      await addDocument("authors", {
        name: form.name.trim(),
        designation: form.designation.trim(),
        bio: form.bio.trim(),
        imageURL,
      });
      onSave();
    } catch (err) {
      console.error(err);
      setError("Save failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
          padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#c53030",
        }}>{error}</div>
      )}
      {uploading && <ProgressBar value={progress} label="Saving author..." />}

      <FileUpload
        label="Profile Photo"
        accept="image/*"
        onFile={f => { set("imageFile", f); set("imagePreview", URL.createObjectURL(f)); }}
        preview={form.imagePreview}
        hint="Author profile photo — JPG, PNG (square recommended)"
      />
      <Input label="Full Name" value={form.name} onChange={v => set("name", v)} required placeholder="e.g. Dr. Priya Patil" />
      <Input label="Designation / Title" value={form.designation} onChange={v => set("designation", v)} placeholder="e.g. Senior Agricultural Scientist" />
      <Textarea label="Bio" value={form.bio} onChange={v => set("bio", v)} rows={4} placeholder="Short author biography..." />

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 14, borderTop: "1px solid #eee" }}>
        <Btn onClick={onClose} variant="secondary" disabled={uploading}>Cancel</Btn>
        <Btn onClick={handleSave} disabled={uploading}>👤 Save Author</Btn>
      </div>
    </div>
  );
}

export default function AuthorsPage({ authors, isAdmin }) {
  const [showForm, setShowForm] = useState(false);

  const handleDelete = async (author) => {
    if (!window.confirm(`Remove author "${author.name}"?`)) return;
    try {
      if (author.imageURL) await deleteFile(author.imageURL);
      await deleteDocument("authors", author.id);
    } catch (err) {
      alert("Delete failed.");
    }
  };

  return (
    <div>
      {!isAdmin && <InfoBanner type="warning">👁 View-only mode.</InfoBanner>}

      <SectionHeader
        title="Authors"
        action={isAdmin && <Btn onClick={() => setShowForm(true)}>+ Add Author</Btn>}
      />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
        {authors.map(author => (
          <div key={author.id} style={{
            background: "#fff", border: "1px solid #e8f0e9", borderRadius: 14, padding: 18,
          }}>
            {/* Avatar + name */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
              {author.imageURL ? (
                <img
                  src={author.imageURL} alt={author.name}
                  style={{ width: 54, height: 54, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "2px solid #b2d8b7" }}
                />
              ) : (
                <div style={{
                  width: 54, height: 54, borderRadius: "50%", background: "linear-gradient(135deg, #e8f5e9, #c8e6c9)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
                  border: "2px solid #b2d8b7",
                }}>👤</div>
              )}
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "#1a1a1a" }}>{author.name}</div>
                {author.designation && (
                  <div style={{ fontSize: 12, color: "#2d7a3a", fontWeight: 600 }}>{author.designation}</div>
                )}
              </div>
            </div>

            {/* Bio */}
            {author.bio ? (
              <p style={{ fontSize: 13, color: "#666", margin: "0 0 12px", lineHeight: 1.6 }}>{author.bio}</p>
            ) : (
              <p style={{ fontSize: 13, color: "#bbb", margin: "0 0 12px", fontStyle: "italic" }}>No bio added.</p>
            )}

            {isAdmin && (
              <Btn variant="danger" style={{ fontSize: 12, padding: "6px 12px", width: "100%" }}
                onClick={() => handleDelete(author)}>
                🗑 Remove Author
              </Btn>
            )}
          </div>
        ))}

        {!authors.length && (
          <div style={{ gridColumn: "1 / -1" }}>
            <EmptyState
              icon="👤"
              message={isAdmin ? "No authors yet. Click '+ Add Author' to create author profiles." : "No authors available."}
            />
          </div>
        )}
      </div>

      {showForm && (
        <Modal title="Add New Author" onClose={() => setShowForm(false)}>
          <AuthorForm onSave={() => setShowForm(false)} onClose={() => setShowForm(false)} />
        </Modal>
      )}
    </div>
  );
}
